<script lang="ts" setup>
import { computed, defineEmits, type PropType, toRef } from 'vue'

import { storeToRefs } from 'pinia'

import type { ShipType } from '@/stores/type/ship.type'
import type { ShipAPIType } from '@/stores/type/shipAPI.type'
import ShipParameterTable from '@/components/ship-importer/ShipParameterTable.vue'
import { useShipAPIStore } from '@/stores/shipAPI'

const shipAPIStore = useShipAPIStore()

const { selectedItems } = storeToRefs(shipAPIStore)

const props = defineProps({
  item: { type: Object as PropType<ShipAPIType>, required: true },
  isCurrentItem: { type: Boolean, default: false }
})

const item = toRef(props, 'item')

const open = ref(false)

const KEY: (keyof ShipType)[] = [
  'name',
  'length_overall',
  'breadth',
  'main_engine_power',
  'rudder',
  'main_engine_type',
  'transverse_thruster_power',
  'transverse_thruster_type'
]

const getFieldType = (field: string) => {
  return shipAPIStore.fields[field]?.type
}

const getChoices = (key: keyof ShipType) => {
  return shipAPIStore.fields[key]?.choices ?? []
}

const getValue = (key: keyof ShipType): string => {
  if (getFieldType(key) === 'choice') {
    return getChoices(key).find((choice) => choice.value === item.value[key])?.display_name ?? ''
  }
  return item.value[key] as string
}

const isSelected = computed<boolean>({
  get: () => selectedItems.value?.includes(item.value) || false,
  set: (value) => {
    shipAPIStore.updateSelectedItems(value, [item.value])
  }
})

function toggleOpen(): void {
  if (item.value.has_armour_param) {
    open.value = !open.value
  }
}

const validationStatus = computed(() => {
  return shipAPIStore.getValidationStatus(item.value)
})

const emit = defineEmits(['click-row'])
</script>

<template>
  <tr
    v-if="item"
    @click.stop="emit('click-row')"
    :class="[isCurrentItem ? 'selected-item' : '', 'cursor-pointer']"
    v-ripple
  >
    <td class="pl-4 pr-0">
      <v-checkbox v-model="isSelected" density="compact" hide-details @click.stop="" />
    </td>
    <td v-for="key in KEY" :key="key" :class="key === 'name' ? '' : 'text-center'">
      {{ getValue(key) }}
    </td>
    <td class="text-center">
      <v-icon v-if="validationStatus" color="green">mdi-check-circle</v-icon>
    </td>
    <td>
      <v-btn
        :icon="open ? `mdi-chevron-up` : `mdi-chevron-right`"
        size="40"
        class="my-1"
        variant="text"
        @click.stop="toggleOpen"
      >
      </v-btn>
    </td>
  </tr>
  <tr v-if="item && item.has_armour_param" class="tr--no-hover">
    <td v-if="open" class="pl-0 pr-0" colspan="999" @click.stop="">
      <ShipParameterTable :ship="item" :fields="shipAPIStore.fields"></ShipParameterTable>
      <v-divider :thickness="2"></v-divider>
    </td>
  </tr>
</template>
